import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Calendar, CheckCircle, XCircle, Loader2, ExternalLink, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { getGoogleAuthUrl, getGoogleAuthStatus } from '../services/api';

const AdminSetup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, email: null });
  const [authMessage, setAuthMessage] = useState(null);

  useEffect(() => {
    // Check for OAuth callback result
    const authResult = searchParams.get('auth');
    if (authResult === 'success') {
      setAuthMessage({ type: 'success', text: 'Google Calendar connected successfully!' });
    } else if (authResult === 'error') {
      setAuthMessage({ type: 'error', text: 'Failed to connect Google Calendar. Please try again.' });
    }

    // Check current calendar connection status
    checkCalendarStatus();
  }, [searchParams]);

  const checkCalendarStatus = async () => {
    try {
      const status = await getGoogleAuthStatus();
      setCalendarStatus(status);
    } catch (error) {
      console.error('Error checking calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    setConnecting(true);
    try {
      const { authorization_url } = await getGoogleAuthUrl();
      // Redirect to Google OAuth
      window.location.href = authorization_url;
    } catch (error) {
      console.error('Error getting auth URL:', error);
      setAuthMessage({ type: 'error', text: 'Failed to initiate connection. Please try again.' });
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDF9F5]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2F5233]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDF9F5] via-[#E8EDE9] to-[#FDF9F5] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#2F5233]/10 rounded-full mb-4">
            <Shield className="w-8 h-8 text-[#2F5233]" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-[#2F5233]">Admin Setup</h1>
          <p className="text-gray-600 mt-2">Configure your clinic's integrations</p>
        </div>

        {/* Auth Message */}
        {authMessage && (
          <Card className={`mb-6 border-0 shadow-lg ${
            authMessage.type === 'success' 
              ? 'bg-green-50 border-l-4 border-l-green-500' 
              : 'bg-red-50 border-l-4 border-l-red-500'
          }`}>
            <CardContent className="p-4 flex items-center gap-3">
              {authMessage.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              <p className={authMessage.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                {authMessage.text}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Google Calendar Card */}
        <Card className="border-0 shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl text-[#2F5233]">Google Calendar</CardTitle>
                <CardDescription>
                  Connect to manage appointment scheduling
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {calendarStatus.connected ? (
              <div className="bg-green-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Connected</p>
                    <p className="text-sm text-green-600">{calendarStatus.email}</p>
                  </div>
                </div>
                <p className="text-sm text-green-700 mt-3">
                  Your calendar is connected. New confirmed appointments will automatically appear in your Google Calendar.
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4 border-green-300 text-green-700 hover:bg-green-100"
                  onClick={handleConnectCalendar}
                  disabled={connecting}
                >
                  {connecting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  Reconnect Calendar
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-amber-800 font-medium">Calendar Not Connected</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Connect your Google Calendar to enable automatic slot availability and appointment creation.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>What this enables:</strong>
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#2F5233]" />
                      Read your calendar to show accurate availability
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#2F5233]" />
                      Create calendar events for confirmed appointments
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#2F5233]" />
                      Auto-generate Google Meet links for online consultations
                    </li>
                  </ul>
                </div>

                <Button 
                  className="w-full bg-[#2F5233] hover:bg-[#2F5233]/90 text-white"
                  onClick={handleConnectCalendar}
                  disabled={connecting}
                  data-testid="connect-calendar-btn"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4 mr-2" />
                      Connect Google Calendar
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-gray-500 text-center">
                  You'll be redirected to Google to authorize access. Sign in with <strong>drjenicebhatia@gmail.com</strong>.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Button 
            variant="ghost" 
            className="text-[#2F5233]"
            onClick={() => navigate('/')}
          >
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSetup;
